import { useTranslation } from "react-i18next";
import { memo, useCallback, useMemo } from "react";

import type { IPasswordStrengthProps } from "./types";
import classes from "./styles.module.scss";
import { Line } from "./line";

import { Typography } from "@/components/atoms/typography";
import { Icon } from "@/components/atoms/icon";
import { getThemeColorUtil } from "@/theme";

export const PasswordStrength = memo(function PasswordStrength(
  props: IPasswordStrengthProps,
) {
  const { password } = props;
  const { t } = useTranslation("component");

  const minimumLength: boolean = password.length > 7;
  const hasUppercase: boolean = /[A-Z]/.test(password);
  const hasDigit: boolean = /\d/.test(password);

  const strength = [hasUppercase, hasDigit, minimumLength].filter(
    Boolean,
  ).length;

  const getIcon = useCallback((condition: boolean) => {
    return condition ? (
      <Icon
        name={"CheckboxCircleFill"}
        color={"state-success-base"}
        fontSize={"fs-16"}
      />
    ) : (
      <Icon
        name={"CloseCircleFill"}
        color={"icon-soft-400"}
        fontSize={"fs-16"}
      />
    );
  }, []);

  const getIconContent = useMemo(() => {
    const hasUppercaseIcon = getIcon(hasUppercase);
    const hasDigitIcon = getIcon(hasDigit);
    const minimumLengthIcon = getIcon(minimumLength);

    return { hasUppercaseIcon, hasDigitIcon, minimumLengthIcon };
  }, [getIcon, hasUppercase, hasDigit, minimumLength]);

  const getLineColor = useCallback(
    (index: number) => {
      if (index < strength) {
        switch (index) {
          case 0:
            return getThemeColorUtil("state-error-base").bgClass;
          case 1:
            return getThemeColorUtil("state-warning-base").bgClass;
          case 2:
            return getThemeColorUtil("state-success-base").bgClass;
        }
      }
      return getThemeColorUtil("bg-soft-200").bgClass;
    },
    [strength],
  );

  const getLineColorContent = useMemo(() => {
    const line0 = getLineColor(0);
    const line1 = getLineColor(1);
    const line2 = getLineColor(2);

    return { line0, line1, line2 };
  }, [getLineColor]);

  return (
    <div className={classes.passwordStrength}>
      <div className={classes.passwordStrength_statusLines}>
        <Line lineColor={getLineColorContent.line0} />
        <Line lineColor={getLineColorContent.line1} />
        <Line lineColor={getLineColorContent.line2} />
      </div>

      <Typography fontType={"paragraph-x-small"} themeColor={"text-sub-600"}>
        {t("passwordStrength.description")}
      </Typography>

      <div className={classes.passwordStrength_rule}>
        {password ? (
          getIconContent.hasUppercaseIcon
        ) : (
          <Icon
            name={"CheckboxCircleFill"}
            color={"icon-soft-400"}
            fontSize={"fs-16"}
          />
        )}
        <Typography fontType={"paragraph-x-small"} themeColor={"text-sub-600"}>
          {t("passwordStrength.containUppercase")}
        </Typography>
      </div>

      <div className={classes.passwordStrength_rule}>
        {password ? (
          getIconContent.hasDigitIcon
        ) : (
          <Icon
            name={"CheckboxCircleFill"}
            color={"icon-soft-400"}
            fontSize={"fs-16"}
          />
        )}
        <Typography fontType={"paragraph-x-small"} themeColor={"text-sub-600"}>
          {t("passwordStrength.containNumber")}
        </Typography>
      </div>

      <div className={classes.passwordStrength_rule}>
        {password ? (
          getIconContent.minimumLengthIcon
        ) : (
          <Icon
            name={"CheckboxCircleFill"}
            color={"icon-soft-400"}
            fontSize={"fs-16"}
          />
        )}
        <Typography fontType={"paragraph-x-small"} themeColor={"text-sub-600"}>
          {t("passwordStrength.containEightCharacters")}
        </Typography>
      </div>
    </div>
  );
});
PasswordStrength.displayName = "PasswordStrength";
